import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  COMMUNITY_NAME,
  COMMUNITY_REGISTERED_NAME,
  LANGUAGE,
  UNITS,
  ADDRESS_1,
  ADDRESS_2,
  ZIPCODE,
  STATE,
  CITY,
  GENERAL_PHONE,
  EMERGENCY_PHONE,
  MANAGER_NAME,
  MANAGER_PHONE,
  CONTACT_EMAIL,
  WEBSITE,
} from "./consts";
import {
  useGetCommunityPortalDetails,
  useGetUserById,
  useGetUsersList,
} from "@/services";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getRequiredFields, roleMap } from "@/lib/utils";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { formValues, schema } from "./validations";

const useStep1 = () => {
  const { action } = useParams();
  const isEditDetails = action === "edit-details";
  const [stateKey, setStateKey] = useState<number>(Math.random() * 3);
  const [managerKey, setManagerKey] = useState<number>(Math.random() * 3);
  const stateList = useSelector(
    (state: RootState) => state.currentData.stateData
  );
  const currentRole = useSelector(
    (state: RootState) => state.currentData.currentRole
  );

  const communityData = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const form = useForm<formValues>({
    defaultValues: {
      [COMMUNITY_NAME]: "",
      [COMMUNITY_REGISTERED_NAME]: undefined,
      [LANGUAGE]: "EN",
      [UNITS]: "",
      [ADDRESS_1]: "",
      [ADDRESS_2]: "",
      [ZIPCODE]: "",
      [STATE]: "",
      [CITY]: "",
      [GENERAL_PHONE]: undefined,
      [EMERGENCY_PHONE]: "",
      [MANAGER_NAME]: "",
      [MANAGER_PHONE]: undefined,
      [CONTACT_EMAIL]: "",
      [WEBSITE]: undefined,
    },
    resolver: zodResolver(schema),
  });

  // Dynamically determine required fields
  const requiredFields = getRequiredFields(schema);

  const {
    data: communityDetail,
    isLoading: isDetailLoading,
    isRefetching: isDetailRefetching,
  } = useGetCommunityPortalDetails({
    community_id: communityData?.communityId || "",
    role: roleMap[currentRole],
    queryParams: {
      enabled: !!(communityData?.communityId && currentRole),
    },
  });

  useEffect(() => {
    if (communityDetail) {
      form.reset({
        [COMMUNITY_NAME]: communityDetail?.data?.[COMMUNITY_NAME] || "",
        [COMMUNITY_REGISTERED_NAME]:
          communityDetail?.data?.[COMMUNITY_REGISTERED_NAME] || undefined,
        [LANGUAGE]: communityDetail?.data?.[LANGUAGE] || "EN",
        [UNITS]: communityDetail?.data?.[UNITS]?.toString() || "",
        [ADDRESS_1]: communityDetail?.data?.[ADDRESS_1] || "",
        [ADDRESS_2]: communityDetail?.data?.[ADDRESS_2] || "",
        [ZIPCODE]: communityDetail?.data?.[ZIPCODE] || "",
        [STATE]:
          stateList?.find(
            (state: { name: string; code: string }) =>
              state.name == communityDetail?.data?.[STATE]?.toString() ||
              state.code == communityDetail?.data?.[STATE]?.toString()
          )?.code || "",
        [CITY]: communityDetail?.data?.[CITY]?.toString() || "",
        [GENERAL_PHONE]: communityDetail?.data?.[GENERAL_PHONE] || undefined,
        [EMERGENCY_PHONE]: communityDetail?.data?.[EMERGENCY_PHONE] || "",
        [MANAGER_NAME]: communityDetail?.data?.[MANAGER_NAME]?.toString() || "",
        [MANAGER_PHONE]: communityDetail?.data?.[MANAGER_PHONE] || undefined,
        [CONTACT_EMAIL]: communityDetail?.data?.[CONTACT_EMAIL] || "",
        [WEBSITE]: communityDetail?.data?.[WEBSITE] || undefined,
      });
      setStateKey(Math.random() * 3);
    }
  }, [communityDetail]);

  const { data: managerList, isLoading: isManagerLoading } = useGetUsersList({
    data: {
      filters: [
        {
          field: "role",
          value: "ROLE_EMPLOYEE",
          operator: "EQUAL",
        },
      ],
      pageNumber: 0,
      pageSize: 1000,
    },
  });

  const managers = managerList?.data || [];
  const managerOptions = managers.map(
    (user: { userId: string | number; name: string; userName: string }) => ({
      label: `${user.name || ""} (${user.userName})`,
      value: user.userId.toString(),
    })
  );
  const managerId = communityDetail?.data?.communityManagerId?.toString();

  if (managerId && !managers.some((user: { userId: string | number }) => user.userId.toString() === managerId)) {
    managerOptions.push({
      label: communityDetail?.data?.communityManagerName || "",
      value: managerId,
    });
  }

  useEffect(() => {
    if (managerList) {
      setManagerKey(Math.random() * 2);
    }
  }, [managerList]);

  const { data: userDetail, isLoading: isLoadingUser } = useGetUserById({
    id: form.watch(MANAGER_NAME) as string,
    queryParams: {
      enabled: !!form.watch(MANAGER_NAME),
    },
  });

  useEffect(() => {
    if (userDetail) {
      form.setValue(MANAGER_PHONE, userDetail?.data?.phoneNumber);
    }
  }, [userDetail]);

  return {
    form,
    requiredFields,
    stateList,
    managerOptions,
    isManagerLoading,
    isEditDetails,
    stateKey,
    isDetailLoading,
    isDetailRefetching,
    managerKey,
    isLoadingUser,
  };
};

export default useStep1;
